import React, { useCallback, useEffect, useRef, useState } from "react";

import type ReactGridLayout from "react-grid-layout";
import { useTranslation } from "react-i18next";
import { cn } from "senf-atomic-design-system/src/util/classNameConstructor";
import { getMediaObjects, useGlobalState } from "senf-shared";
import type { Project } from "senf-shared/types";
import { tv } from "tailwind-variants";
import { v4 as uuidv4 } from "uuid";
import { useImageMetaData } from "../../../../hooks/useImageMetaData";
import DraggableImages from "../../../atoms/draggableGrid/DraggableImages";
import Button from "../../../core/button/Button";
import Icon from "../../../core/icon/Icon";
import { CopyrightIcon } from "../../copyright/CopyrightIcon";
import type { ImageInput } from "../../imageModifier/ImagesUploadModal";
import ModifyImageButtons from "../../imageModifier/ModifyImageButtons";
import HorizontalScrollTeaser from "../../layout/HorizontalScrollTeaser";
import { useSurveyImageSettings } from "../hooks";
import type { ImageSettings, Page, SurveyImage } from "../survey.config";
import { findSmallerStackX, useEditQuestion } from "../utils";

const SurveyImageStyles = tv({
  slots: {
    outer: "w-full",
    container: "relative h-full w-full",
    wrapper: ["relative group/option flex justify-center items-center"],
    image: "object-cover",
  },
  variants: {
    hidden: {
      true: {
        container: "pointer-events-none absolute opacity-0",
      },
    },
    gallery: {
      true: {
        outer: "h-full",
      },
      false: {
        container: "flex items-center justify-center",
        image: "h-full",
      },
    },
    full: {
      false: {
        outer: "flex justify-center max-h-full",
        wrapper: "max-h-full rounded-2xl",
        image:
          "h-auto max-h-[calc(var(--h-screen,100vh)-5rem)] w-auto rounded-2xl",
      },
      true: {
        container: "h-full w-full",
        wrapper: "h-full",
        image:
          "h-full w-full max-h-[var(--h-screen,100vh)] w-[var(--w-screen,100vw)]",
      },
    },
    mode: {
      display: {
        image: "duration-500 ease-expressive-out transition-all",
      },
      edit: {
        wrapper:
          "duration-1000 ease-expressive-out transition-[height,max-height] will-change-[height,max-height]",
        image: "opacity-0",
      },
    },
  },
  compoundVariants: [
    {
      mode: "edit",
      full: false,
      wrapper: "rounded-2xl",
    },
  ],
});

export const ImageNumber = ({
  className,
  children,
}: React.HTMLAttributes<HTMLButtonElement>) => {
  return (
    <Button
      className={cn(
        "pointer-events-none absolute left-0 top-0 max-w-full rounded-2xl bg-neutral-500/40 font-bold text-white backdrop-blur-sm backdrop-saturate-150",
        className,
      )}
      size="sm"
      variant="shapeless"
    >
      {children}
    </Button>
  );
};

// tries handle both arrays of images and a string url
interface RenderImageProps {
  images?: ImageSettings["items"];
  full?: boolean;
  hidden?: boolean;
  loadingImage?: number | boolean;
  mode: "edit" | "display";
  onLoad?: () => void;
  pageIndex: number;
  setPages?: React.Dispatch<React.SetStateAction<Page[]>>;
  outerHeight: number;
  imageFocus?: number | false;
  isMobile?: boolean;
  page: Page;
  project: Project;
  surveyId: string;
}

interface ImageObject {
  i: string;
  url: string;
  name: string;
  alt: string;
  path: string;
  w: number;
  h: number;
  x: number;
  y: number;
  copyright?: string;
}

const RenderImages = ({
  images,
  full,
  hidden,
  loadingImage,
  onLoad,
  mode,
  pageIndex,
  setPages,
  outerHeight,
  imageFocus,
  isMobile,
  page,
  project,
  surveyId,
}: RenderImageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const { metadata: imageMetadata, fetchMetadata } = useImageMetaData({
    images: images || [],
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchMetadata();
  }, []);

  const { container, wrapper, image } = SurveyImageStyles({
    mode,
    full: !!full,
    hidden: !!hidden,
    gallery: images?.length !== 1,
  });
  const { updateQuestion } = useEditQuestion({
    pageIndex,
    setPages,
  });
  const { rowGrid } = React.useMemo(useSurveyImageSettings, []);
  const rowHeight = React.useRef(10);

  const updateLayout = useCallback<
    NonNullable<ReactGridLayout.ReactGridLayoutProps["onLayoutChange"]>
  >(
    (l) => {
      const newLayout = images?.map((e) => {
        const { w, h, x, y } = l.find((f) => f.i === e.i) || {};
        return { ...e, w, h, x, y };
      }) as NonNullable<Page["images"]>["items"];
      updateQuestion(
        {
          ...page.images,
          items: newLayout,
        },
        "images",
      );
    },
    [updateQuestion, images, page.images],
  );

  const layout: SurveyImage[] =
    images
      ?.sort((a, b) => a.y + a.x - (b.y + b.x))
      ?.filter(Boolean)
      ?.map((item = {} as SurveyImage) => ({
        ...item,
        i: item.i,
        x: item.x || 0,
        y: item.y || 0,
        w: item.w || 2,
        h: Math.min(Math.max(item.h || 5, 3), 15),
        minH: 3,
        maxH: 15,
      })) || [];

  const [singleImage] = images || [];

  const { x } = findSmallerStackX({ items: images || [] });

  const loadingPlaceholders: ReactGridLayout.Layout[] = [
    ...Array(loadingImage).keys(),
  ].map((index) => ({
    i: (layout.length + index).toString(),
    w: 2,
    h: 5,
    x: loadingImage === 1 ? x : index % 2,
    alt: (index % 2) + 1,
    y: Number.POSITIVE_INFINITY,
  }));

  const augmentedLayout = loadingImage
    ? [...layout, ...loadingPlaceholders]
    : layout;

  const [overflowing, setOverflowing] = useState(true);

  useEffect(() => {
    if (imageFocus === false || imageFocus === undefined) return;
    ref.current
      ?.querySelector(`.react-grid-item:nth-child(${imageFocus + 1})`)
      ?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
  }, [imageFocus]);

  if (!images?.length) return null;

  const layoutClassName = overflowing
    ? "top-0 translate-y-0"
    : "h-full top-1/2 -translate-y-1/2";

  const handleFileChange = async (filesOrImages: ImageInput[], i: string) => {
    if (!project) return;

    try {
      const imageObjects: ImageObject[] = [];

      if (Array.isArray(filesOrImages)) {
        // Handle both file uploads and Unsplash images
        const item = filesOrImages[0]; // Only take the first image when changing

        const {
          x = 0,
          y = 0,
          w = 2,
          h = 5,
        } = images.find((e) => e.i === i) || {};

        const newImage = {
          i: uuidv4(),
          url: "",
          name: `Image ${i}`,
          altText: "",
          path: "",
          x,
          y,
          w,
          h,
        };

        if (item instanceof File) {
          // Handle uploaded files
          const [object] = await getMediaObjects({
            fileList: [item],
            setPath: () =>
              `organizationsData/${project.organizationId}/projects/${project.projectId}/surveys/${surveyId}/surveyImagesUpload/`,
          });
          newImage.url = object.url;
          newImage.path = object.path;
        } else {
          if (item.url) newImage.url = item.url;
          if (item.altText) newImage.altText = item.altText;
        }

        imageObjects.push(newImage);
      }

      if (imageObjects.length > 0) {
        const newImages = [...(images || [])];
        // Replace the image at the specified index
        const index = newImages.findIndex((e) => e.i === i);
        newImages[index] = imageObjects[0];
        updateQuestion(
          {
            ...page.images,
            items: newImages,
          },
          "images",
        );
      }
    } catch (error) {
      console.error("Error handling file change:", error);
    }
  };

  const handleSetFormat = (value: boolean) => {
    setPages?.((pages) => {
      return pages.map((q) => {
        const _page = { ...q };
        if (_page.uid === page.uid) {
          _page.images ??= { items: [] };
          if (value) {
            _page.images.format = "fill";
          } else {
            _page.images.format = "fit";
          }
        }
        return _page;
      });
    });
  };

  const allowedFileTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
  ];

  return (layout?.length || 0) > 1 ? (
    <div ref={ref} className={cn("h-full w-full relative")}>
      {isMobile ? (
        <HorizontalScrollTeaser>
          {layout.map((img, index) => (
            <div
              key={img.i}
              className={cn(
                typeof imageFocus === "number" &&
                  imageFocus !== index &&
                  "opacity-30 blur-sm",
                "react-grid-item relative shrink-0 scroll-m-5 group/option",
              )}
            >
              <img
                className={cn("h-full rounded-2xl")}
                src={img?.url}
                alt={
                  (img &&
                    ((img.altText && translation?.[img.altText]) ||
                      img.altText ||
                      imageMetadata[img.url]?.customMetadata?.altText)) ||
                  t("global:image")
                }
                onLoad={onLoad}
                aria-describedby={`figcaption-${img.i}`}
              />
              {mode === "display" && img?.copyright && (
                <CopyrightIcon
                  copyright={img?.copyright}
                  className={cn(
                    page?.images?.format === "fill" && !isMobile
                      ? "m-5"
                      : undefined,
                  )}
                  captionId={`figcaption-${img.i}`}
                />
              )}
            </div>
          ))}
        </HorizontalScrollTeaser>
      ) : (
        <DraggableImages
          project={project}
          surveyId={surveyId}
          currentPage={page}
          mode={mode}
          className={cn(container())}
          classNames={{
            layout: cn("relative [direction:ltr]", layoutClassName),
          }}
          layout={augmentedLayout}
          rowGrid={rowGrid}
          updateLayout={updateLayout}
          updateQuestion={updateQuestion}
          rowHeight={rowHeight.current}
          onLoad={onLoad}
          imageFocus={imageFocus}
          onHeightChange={(height) => {
            setOverflowing(height > outerHeight - 80);
          }}
          handleFileChange={handleFileChange}
          imageMetadata={imageMetadata}
          isMobile={isMobile}
        />
      )}
    </div>
  ) : (
    <div className={cn(container())}>
      {loadingImage ? (
        <div className="bg-surface-subdued flex aspect-[9/16] max-h-full w-full items-center justify-center rounded-2xl">
          <Icon name="loading" />
        </div>
      ) : (
        <div
          className={wrapper()}
          style={
            mode === "edit" && images[0]?.url
              ? {
                  backgroundImage: `url("${singleImage.url}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }
              : undefined
          }
        >
          {mode === "display" && singleImage.copyright && (
            <CopyrightIcon
              copyright={singleImage.copyright}
              className={
                page?.images?.format === "fill" && !isMobile ? "m-5" : undefined
              }
            />
          )}
          {mode === "edit" && (
            <ModifyImageButtons
              project={project}
              fileTypes={allowedFileTypes}
              surveyId={surveyId}
              inputRef={ref}
              imageUrl={singleImage.url}
              altText={singleImage.altText}
              altTextPlaceholder={
                singleImage.url &&
                imageMetadata[singleImage.url]?.customMetadata?.altText
              }
              copyright={singleImage.copyright}
              onChangeImage={(vals) => handleFileChange?.(vals, singleImage.i)}
              onRemoveImage={() => {
                updateQuestion(undefined, "images");
              }}
              onAltTextChange={(value) => {
                updateQuestion(
                  {
                    ...page.images,
                    items: [{ ...singleImage, alt: value }],
                  },
                  "images",
                );
              }}
              onCopyrightChange={(value) => {
                updateQuestion(
                  {
                    ...page.images,
                    items: [{ ...singleImage, copyright: value }],
                  },
                  "images",
                );
              }}
              onFormatChange={(value) => {
                handleSetFormat(value);
              }}
              imgFormat={page?.images?.format === "fill"}
              showFormat={page?.images?.items?.length === 1}
              className="right-2 top-2 z-10 opacity-0 group-hover/option:opacity-100"
              maxItems={1}
              saveToPath={`surveys/${surveyId}`}
            />
          )}
          <img
            className={image()}
            src={singleImage.url}
            alt={
              (singleImage.altText && translation?.[singleImage.altText]) ||
              singleImage.altText ||
              (singleImage.url &&
                imageMetadata[singleImage.url]?.customMetadata?.altText) ||
              t("global:image")
            }
            onLoad={onLoad}
          />
        </div>
      )}
    </div>
  );
};

RenderImages.displayName = "RenderImages";

interface SurveyImagesProps {
  page: Page;
  setPages?: React.Dispatch<React.SetStateAction<Page[]>>;
  pageIndex: number;
  loadingImage?: number | boolean;
  isMobile?: boolean;
  mode: "edit" | "display";
  outerHeight: number;
  project: Project;
  surveyId: string;
  imageFocus?: number | false;
}

const SurveyImages = ({
  page,
  loadingImage,
  isMobile,
  mode,
  setPages,
  pageIndex,
  imageFocus,
  project,
  surveyId,
  ...props
}: SurveyImagesProps) => {
  const full = !!(isMobile || page?.images?.format === "fill");
  const { outer } = SurveyImageStyles({
    full,
  });

  return (
    <div className={cn(outer())} key={page.uid}>
      <RenderImages
        images={page?.images?.items || []}
        mode={mode}
        full={full}
        isMobile={isMobile}
        setPages={setPages}
        pageIndex={pageIndex}
        loadingImage={loadingImage}
        outerHeight={props.outerHeight}
        imageFocus={imageFocus}
        page={page}
        project={project}
        surveyId={surveyId}
      />
    </div>
  );
};

export default SurveyImages;
